import React, { useState } from "react";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { McpWizardModal } from "../../components/McpWizardModal/mcp-wizard-modal";
import {
    MCP_WIZARD_DEFAULT_REDIRECT_URIS,
    MCP_WIZARD_DEFAULT_SCOPES,
} from "../../consts";
import {
    DEFAULT_MCP_CLIENT,
    getMcpClient,
    type McpClientId,
    type McpCredential,
} from "../../lib/mcp-clients";
import * as Analytics from "../../analytics";

// // // //

/**
 * McpWizardModalProvider
 * Provider for the MCP wizard modal. Generates the credential the chosen
 * client needs: an OAuth client + secret for Claude, or an `sk-rwa-` API key
 * for the API-key clients (Claude Code, Cursor, Codex Desktop). The client is
 * chosen inside the wizard (seeded from wherever the wizard was opened), so
 * `generate` takes it as an argument.
 */
export function McpWizardModalProvider(props: {
    children: ({
        openWizard,
    }: {
        openWizard: (client: McpClientId) => void;
    }) => React.ReactNode;
}) {
    const { user } = useUser();
    const [wizardOpen, setWizardOpen] = useState(false);
    const [initialClient, setInitialClient] =
        useState<McpClientId>(DEFAULT_MCP_CLIENT);

    const createClientMutation = api.oauthClients.create.useMutation();
    const createSecretMutation = api.oauthClients.createSecret.useMutation();
    const createApiKeyMutation = api.apiKeys.create.useMutation();

    const handleGenerate = async (
        name: string,
        client: McpClientId,
    ): Promise<McpCredential> => {
        const authType = getMcpClient(client).auth;
        Analytics.trackCreateConnectionAttempted({ client, authType });

        try {
            let credential: McpCredential;

            if (authType === "oauth") {
                const oauthClient = await createClientMutation.mutateAsync({
                    name,
                    redirectUris: [...MCP_WIZARD_DEFAULT_REDIRECT_URIS],
                    scopes: [...MCP_WIZARD_DEFAULT_SCOPES],
                    organizationId: user.organization.id,
                    clientType: "confidential",
                });

                const secret = await createSecretMutation.mutateAsync({
                    clientId: oauthClient.id,
                });

                credential = {
                    kind: "oauth",
                    clientId: oauthClient.clientId,
                    clientSecret: secret.plaintext,
                    name: oauthClient.name,
                };
            } else {
                const apiKey = await createApiKeyMutation.mutateAsync({
                    name,
                    organizationID: user.organization.id,
                });

                credential = {
                    kind: "api-key",
                    key: apiKey.key,
                    name: apiKey.name,
                };
            }

            Analytics.trackCreateConnectionSuccess({ client, authType });
            return credential;
        } catch (error) {
            Analytics.trackCreateConnectionError({ client, authType });
            throw error;
        }
    };

    const openWizard = (nextClient: McpClientId) => {
        Analytics.trackOpenConnectWizard({ client: nextClient });
        setInitialClient(nextClient);
        setWizardOpen(true);
    };

    return (
        <>
            {props.children({ openWizard })}

            <McpWizardModal
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                generate={handleGenerate}
                initialClient={initialClient}
                generating={
                    createClientMutation.isPending ||
                    createSecretMutation.isPending ||
                    createApiKeyMutation.isPending
                }
            />
        </>
    );
}
